import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link, Navigate } from 'react-router-dom';
import { fetchItemsWithCache } from '../../utils/apiCache';
import { getAdminSession } from '../../utils/adminSession';
import { getAdminAuthHeaders, hasAdminCredentials } from '../../utils/adminAuth';
import { addTeamLogoUrl, entriesUrl, CACHE_KEYS } from '../../utils/apiEndpoints';
import { Fixture } from '../../models/Fixture';

const PageContainer = styled.div`
  max-width: 600px;
  margin: 4rem auto;
  padding: 2rem;
  background-color: var(--gray-900);
  color: var(--gray-50);
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
`;

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Label = styled.label`
  font-weight: 600;
`;

const HelperText = styled.span`
  font-size: 0.8rem;
  color: var(--gray);
`;

const RequiredMark = styled.span`
  color: #f97373;
  margin-left: 0.15rem;
`;

const Select = styled.select`
  padding: 0.6rem 0.8rem;
  border-radius: 6px;
  border: 1px solid #444;
  background: #111;
  color: #f9fafb;
`;

const Input = styled.input`
  padding: 0.6rem 0.8rem;
  border-radius: 6px;
  border: 1px solid #444;
  background: #111;
  color: #f9fafb;

  &::-webkit-calendar-picker-indicator {
    filter: invert(1);
  }
`;

const FileInput = styled.input`
  padding: 0.6rem 0.8rem;
  border-radius: 6px;
  border: 1px solid #444;
  background: #111;
  color: #f9fafb;
`;

const SubmitButton = styled.button`
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border: none;
  background: var(--primary);
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.1s ease;

  &:hover:enabled {
    background: var(--primary-light);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  border-radius: 8px;
  background: #3a1010;
  color: #ffd1d1;
`;

const UploadError = styled.div`
  margin-top: 0.75rem;
  padding: 0.75rem;
  border-radius: 8px;
  background: #3a1010;
  color: #ffd1d1;
`;

const ActionsRow = styled.div`
  margin-top: 1rem;
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const SecondaryButton = styled.button`
  padding: 0.6rem 1rem;
  border-radius: 8px;
  border: 1px solid #555;
  background: transparent;
  color: var(--gray-50);
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #222;
  }
`;

const LinkButton = styled(Link)`
  padding: 0.6rem 1rem;
  border-radius: 8px;
  border: 1px solid #555;
  background: transparent;
  color: var(--gray-50);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #222;
  }
`;

type SubmitState = 'idle' | 'submitting' | 'success' | 'error';

const NOT_LISTED_VALUE = '__not_listed__';

const AddTeamLogo: React.FC = () => {
  const session = getAdminSession();

  const [homeTeams, setHomeTeams] = useState<string[]>([]);
  const [loadingTeams, setLoadingTeams] = useState(true);
  const [teamsLoadError, setTeamsLoadError] = useState<string | null>(null);

  const [teamSelection, setTeamSelection] = useState<string>('');
  const [otherTeamName, setOtherTeamName] = useState<string>('');

  const [teamLogoBase64, setTeamLogoBase64] = useState<string | null>(null);
  const [teamLogoFileName, setTeamLogoFileName] = useState<string | null>(null);
  const [isReadingTeamLogo, setIsReadingTeamLogo] = useState(false);
  const [imageInputKey, setImageInputKey] = useState(0);
  const [imageError, setImageError] = useState<string | null>(null);

  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const loadHomeTeams = async () => {
      try {
        setLoadingTeams(true);
        setTeamsLoadError(null);
        if (!hasAdminCredentials(getAdminSession())) return;

        const items = await fetchItemsWithCache<Fixture>(
          entriesUrl('tigers_fixtures'),
          CACHE_KEYS.fixturesTeamLogo,
          5 * 60 * 1000
        );

        const unique = Array.from(new Set((items ?? []).map((f) => f.homeTeam).filter(Boolean))).sort((a, b) =>
          a.localeCompare(b)
        );
        setHomeTeams(unique);
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Unable to load teams.';
        setTeamsLoadError(message);
      } finally {
        setLoadingTeams(false);
      }
    };

    loadHomeTeams();
  }, []);

  const finalTeamName = teamSelection === NOT_LISTED_VALUE ? otherTeamName.trim() : teamSelection.trim();

  const handleTeamLogoFile = (file: File | null) => {
    setImageError(null);

    if (!file) {
      setTeamLogoBase64(null);
      setTeamLogoFileName(null);
      setIsReadingTeamLogo(false);
      return;
    }

    const isPng = file.type === 'image/png' || file.name.toLowerCase().endsWith('.png');
    if (!isPng) {
      setImageError('Team logo image must be a PNG file.');
      return;
    }

    const maxBytes = 400 * 1024;
    if (file.size > maxBytes) {
      setImageError('Team logo image must be 400KB or smaller.');
      return;
    }

    const reader = new FileReader();
    setIsReadingTeamLogo(true);
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') return;

      // data:[mime];base64,XXXXX -> take only XXXXX
      const base64 = result.includes(',') ? result.split(',')[1] : result;
      setTeamLogoBase64(base64);
      setTeamLogoFileName(file.name);
      setIsReadingTeamLogo(false);
    };
    reader.onerror = () => {
      setImageError('Unable to read selected image file.');
      setIsReadingTeamLogo(false);
    };
    reader.readAsDataURL(file);
  };

  const resetForm = () => {
    setTeamSelection('');
    setOtherTeamName('');
    setTeamLogoBase64(null);
    setTeamLogoFileName(null);
    setIsReadingTeamLogo(false);
    setImageInputKey((k) => k + 1);
    setImageError(null);
    setSubmitState('idle');
    setErrorMessage(null);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    setSubmitState('submitting');
    setErrorMessage(null);

    // Persist image validation messages and prevent submitting invalid images.
    if (imageError) {
      setSubmitState('idle');
      return;
    }

    if (isReadingTeamLogo) {
      setImageError('Please wait for the image to finish loading.');
      setSubmitState('idle');
      return;
    }

    if (!teamLogoBase64) {
      setImageError('Team logo image is required.');
      setSubmitState('idle');
      return;
    }

    if (!hasAdminCredentials(session)) {
      setSubmitState('error');
      setErrorMessage('Session expired. Please log in again.');
      return;
    }

    if (!finalTeamName) {
      setSubmitState('idle');
      setErrorMessage('Please select a team name (or choose "Not listed" and enter one).');
      return;
    }

    try {
      const payload = {
        teamName: finalTeamName,
        imageBase64: teamLogoBase64,
      };

      const response = await fetch(addTeamLogoUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAdminAuthHeaders(session),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Failed to add team logo');
      }

      setSubmitState('success');
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Unknown error while adding team logo';
      setErrorMessage(message);
      setSubmitState('error');
    }
  };

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  if (submitState === 'success') {
    return (
      <PageContainer>
        <Title>Team Logo Added</Title>
        <Message>The team logo was added successfully.</Message>
        <ActionsRow>
          <SecondaryButton type="button" onClick={resetForm}>
            Add another logo
          </SecondaryButton>
          <LinkButton to="/admin">Back to admin</LinkButton>
        </ActionsRow>
      </PageContainer>
    );
  }

  if (submitState === 'error') {
    return (
      <PageContainer>
        <Title>Team Logo Not Added</Title>
        <ErrorMessage>
          There was a problem adding the team logo.
          {errorMessage ? <div style={{ marginTop: '0.5rem' }}>{errorMessage}</div> : null}
        </ErrorMessage>
        <ActionsRow>
          <SecondaryButton type="button" onClick={resetForm}>
            Try again
          </SecondaryButton>
          <LinkButton to="/admin">Back to admin</LinkButton>
        </ActionsRow>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Title>Add Team Logo</Title>
      <Form onSubmit={handleSubmit}>
        <FieldGroup>
          <Label htmlFor="teamSelect">
            Team Name<RequiredMark>*</RequiredMark>
          </Label>
          <Select id="teamSelect" value={teamSelection} onChange={(e) => setTeamSelection(e.target.value)} required>
            <option value="">Select a team</option>
            {loadingTeams ? <option value="" disabled>Loading…</option> : null}
            {!loadingTeams &&
              homeTeams.map((team) => (
                <option key={team} value={team}>
                  {team}
                </option>
              ))}
            <option value={NOT_LISTED_VALUE}>Not listed</option>
          </Select>
          {teamsLoadError ? <HelperText style={{ color: '#ffd1d1' }}>{teamsLoadError}</HelperText> : null}
        </FieldGroup>

        {teamSelection === NOT_LISTED_VALUE ? (
          <FieldGroup>
            <Label htmlFor="otherTeam">Team Name (not listed)</Label>
            <Input
              id="otherTeam"
              type="text"
              value={otherTeamName}
              onChange={(e) => setOtherTeamName(e.target.value)}
              required
            />
          </FieldGroup>
        ) : null}

        <FieldGroup>
          <Label htmlFor="teamLogo">
            Team Logo (PNG, max 400KB)<RequiredMark>*</RequiredMark>
          </Label>
          <FileInput
            key={imageInputKey}
            id="teamLogo"
            type="file"
            accept="image/png"
            onChange={(e) => handleTeamLogoFile(e.target.files ? e.target.files[0] : null)}
          />
          {imageError ? <UploadError>{imageError}</UploadError> : null}

          {teamLogoFileName ? (
            <HelperText>
              Selected: <span style={{ color: '#c9f7d6' }}>{teamLogoFileName}</span>
            </HelperText>
          ) : null}

          {isReadingTeamLogo ? <HelperText style={{ marginTop: '0.5rem' }}>Reading image…</HelperText> : null}
        </FieldGroup>

        <SubmitButton
          type="submit"
          disabled={submitState === 'submitting' || isReadingTeamLogo || !!imageError || !teamLogoBase64 || !finalTeamName}
        >
          {submitState === 'submitting' ? 'Submitting…' : 'Add Logo'}
        </SubmitButton>

        <ActionsRow>
          <LinkButton to="/admin">Back to admin</LinkButton>
        </ActionsRow>
      </Form>
    </PageContainer>
  );
};

const Message = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  border-radius: 8px;
  background: #12331a;
  color: #c9f7d6;
`;


export default AddTeamLogo;

